import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Upload, Users, Plane, FileText, Receipt, Building2, Briefcase, Settings, Database, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Backups = () => {
  const [activeTab, setActiveTab] = useState("users");
  
  // Import dialog states
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState('skip');
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importedDataDialogOpen, setImportedDataDialogOpen] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  const tabs = [
    { id: "users", label: "Users", icon: Users, endpoint: "users", fileExt: "users" },
    { id: "agencies", label: "Agencies", icon: Building2, endpoint: "agencies", fileExt: "agencies" },
    { id: "agents", label: "Agents", icon: Users, endpoint: "agents", fileExt: "agents" },
    { id: "flights", label: "Flights", icon: Plane, endpoint: "flights", fileExt: "flights" },
    { id: "reports", label: "Reports", icon: FileText, endpoint: "reports", fileExt: "reports" },
    { id: "invoices", label: "Invoices", icon: Receipt, endpoint: "invoices", fileExt: "invoices" },
    { id: "services", label: "Services", icon: Briefcase, endpoint: "services", fileExt: "services" },
    { id: "settings", label: "Settings", icon: Settings, endpoint: "settings", fileExt: "settings" },
  ];

  const currentTab = tabs.find(t => t.id === activeTab);

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/export-${currentTab.endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = response.headers['content-disposition']?.split('filename=')[1] || `${currentTab.fileExt}_backup_${Date.now()}.vipbackup`;
      link.setAttribute('download', filename.replace(/"/g, ''));
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${currentTab.label} exported successfully!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to export ${currentTab.label.toLowerCase()}`);
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    
    if (!importFile) {
      toast.error('Please select a backup file');
      return;
    }
    
    setImporting(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('import_mode', importMode);
      
      const response = await axios.post(`${API}/admin/import-${currentTab.endpoint}`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const results = response.data.results;
      setImportResults(results);
      setImportFile(null);
      setImportMode('skip');
      
      // Close import dialog
      setImportDialogOpen(false);
      
      // Show imported data in separate dialog
      setImportedDataDialogOpen(true);
      
      toast.success(`${currentTab.label} imported successfully!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to import ${currentTab.label.toLowerCase()}`);
    } finally {
      setImporting(false);
    }
  };

  const handleExportOfflineData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/export/offline-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Download as JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `viphandling-offline-data-${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Data exported for offline app! Import this file into your desktop application.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export offline data');
    }
  };

  const handleFullExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/export-all`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = response.headers['content-disposition']?.split('filename=')[1] || `full_backup_${Date.now()}.vipbackup`;
      link.setAttribute('download', filename.replace(/"/g, ''));
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Full backup exported successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export full backup');
    }
  };

  const handleFullImport = async (e) => {
    e.preventDefault();
    
    if (!importFile) {
      toast.error('Please select a backup file');
      return;
    }
    
    setImporting(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('import_mode', importMode);
      
      const response = await axios.post(`${API}/admin/import-all`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const results = response.data.results;
      setImportResults(results);
      setImportFile(null);
      setImportMode('skip');
      
      setImportDialogOpen(false);
      setImportedDataDialogOpen(true);
      
      toast.success('Full backup imported successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import full backup');
    } finally {
      setImporting(false);
    }
  };

  const handleReconcileFlightInvoices = async () => {
    setReconciling(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/reconcile-flight-invoices`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const stats = response.data.stats;
      if (stats.conflicts_found && stats.conflicts_found.length > 0) {
        toast.success(`Fixed ${stats.flights_marked_invoiced + stats.flights_marked_uninvoiced} flight-invoice conflicts!`);
      } else {
        toast.success('No conflicts found - data is consistent!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reconcile data');
    } finally {
      setReconciling(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#FFFFFF' }}>Data Backups</h1>
          <p style={{ color: '#B0B0B0' }}>Export and import your data for backup and migration</p>
        </div>
      </div>

      {/* Full Backup Section */}
      <Card style={{ background: '#1E2A4A', borderColor: '#4A5A8A', marginBottom: '24px' }}>
        <CardHeader>
          <CardTitle style={{ color: '#FFFFFF', display: 'flex', alignItems: 'center' }}>
            <Database className="mr-2 h-5 w-5" style={{ color: '#10B981' }} />
            Complete System Backup
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-start">
            <div className="flex-1">
              <p className="text-sm mb-2" style={{ color: '#B0B0B0' }}>
                One-click backup of ALL your data: Users, Agencies, Agents, Flights, Reports, Invoices, Services, and Settings.
              </p>
              <p className="text-xs mb-2" style={{ color: '#FB923C' }}>
                ⚡ Recommended for complete system migration or disaster recovery
              </p>
              <p className="text-xs" style={{ color: '#60A5FA' }}>
                💡 <strong>Cross-Instance Transfer:</strong> .vipbackup files are encrypted for security. 
                For transferring between different instances (e.g., deployed → local), import should work automatically. 
                If you get encryption errors, contact support for JSON export option.
              </p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <Button 
                onClick={handleFullExport}
                style={{ background: '#10B981', color: '#FFFFFF' }}
              >
                <Download className="mr-2 h-4 w-4" />
                Export Everything
              </Button>
              <Button 
                onClick={() => {
                  setActiveTab("full");
                  setImportDialogOpen(true);
                }}
                style={{ background: '#3B82F6', color: '#FFFFFF' }}
              >
                <Upload className="mr-2 h-4 w-4" />
                Import Everything
              </Button>
              <Button 
                onClick={handleReconcileFlightInvoices}
                disabled={reconciling}
                style={{ background: '#FB923C', color: '#FFFFFF' }}
                title="Fix data conflicts where flights appear in wrong invoice status"
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${reconciling ? 'animate-spin' : ''}`} />
                {reconciling ? 'Fixing...' : 'Fix Data Conflicts'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Offline Desktop App Section */}
      <Card style={{ background: '#1E2A4A', borderColor: '#10B981', border: '2px solid #10B981', marginBottom: '24px' }}>
        <CardHeader>
          <CardTitle style={{ color: '#FFFFFF', display: 'flex', alignItems: 'center' }}>
            <Download className="mr-2 h-5 w-5" style={{ color: '#10B981' }} />
            🖥️ Desktop App Two-Way Sync
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-start">
            <div className="flex-1">
              <p className="text-sm mb-2" style={{ color: '#B0B0B0' }}>
                Export all data as JSON for the offline desktop app. Use this to sync your online data with the desktop application.
              </p>
              <div className="p-3 rounded mb-3" style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid #10B981' }}>
                <p className="text-xs mb-1" style={{ color: '#10B981' }}>
                  <strong>📥 How to Import in Desktop App:</strong>
                </p>
                <ol className="text-xs" style={{ color: '#B0B0B0', paddingLeft: '16px' }}>
                  <li>1. Click "Export JSON for Desktop" below</li>
                  <li>2. Open the Desktop App → Backups & Sync</li>
                  <li>3. Click "Load Backup File" and select the JSON</li>
                </ol>
              </div>
              <div className="p-3 rounded" style={{ background: 'rgba(251, 146, 60, 0.1)', border: '1px solid #FB923C' }}>
                <p className="text-xs" style={{ color: '#FB923C' }}>
                  <strong>Master Key Login:</strong> Admin / masterkey01*
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Button 
                onClick={handleExportOfflineData}
                style={{ background: '#10B981', color: '#FFFFFF', fontSize: '14px', padding: '12px 20px' }}
              >
                <Download className="mr-2 h-5 w-5" />
                Export JSON for Desktop
              </Button>
              <a 
                href="/desktop-app-offline.zip" 
                download="VIP-Handling-Offline-App.zip"
                className="inline-flex items-center justify-center px-4 py-2 rounded text-sm font-medium"
                style={{ background: '#FB923C', color: '#FFFFFF', textDecoration: 'none' }}
              >
                <Download className="mr-2 h-4 w-4" />
                Download Desktop App
              </a>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Divider */}
      <div className="mb-6">
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px" style={{ background: 'rgba(74, 90, 138, 0.3)' }}></div>
          <p className="text-sm" style={{ color: '#B0B0B0' }}>Or backup individual data types</p>
          <div className="flex-1 h-px" style={{ background: 'rgba(74, 90, 138, 0.3)' }}></div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-8 mb-6" style={{ background: '#1E2A4A' }}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <TabsTrigger 
                key={tab.id} 
                value={tab.id}
                style={{ 
                  color: activeTab === tab.id ? '#FFFFFF' : '#B0B0B0',
                  borderBottom: activeTab === tab.id ? '2px solid #FFFFFF' : 'none'
                }}
              >
                <Icon className="mr-2 h-4 w-4" />
                {tab.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {tabs.map((tab) => (
          <TabsContent key={tab.id} value={tab.id}>
            <Card style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
              <CardHeader>
                <CardTitle style={{ color: '#FFFFFF' }}>{tab.label} Backup</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 rounded" style={{ background: 'rgba(59, 130, 246, 0.1)', borderColor: '#3B82F6', border: '1px solid' }}>
                    <h3 className="text-sm font-semibold mb-2" style={{ color: '#3B82F6' }}>Export {tab.label}</h3>
                    <p className="text-sm mb-4" style={{ color: '#B0B0B0' }}>
                      Download all {tab.label.toLowerCase()} to a backup file.
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        onClick={handleExport}
                        style={{ background: '#10B981', color: '#FFFFFF' }}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Export {tab.label}
                      </Button>
                      {tab.id === 'reports' && (
                        <Button 
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              const response = await axios.get(`${API}/admin/export-reports-json`, {
                                headers: { Authorization: `Bearer ${token}` },
                                responseType: 'blob'
                              });
                              const url = window.URL.createObjectURL(new Blob([response.data]));
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', `reports_json_${Date.now()}.json`);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              toast.success('Reports exported as JSON for cross-instance transfer!');
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Failed to export reports JSON');
                            }
                          }}
                          style={{ background: '#8B5CF6', color: '#FFFFFF' }}
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Export as JSON
                        </Button>
                      )}
                    </div>
                    {tab.id === 'reports' && (
                      <p className="text-xs mt-2" style={{ color: '#8B5CF6' }}>
                        💡 Use JSON export for transferring between different instances (deployed → local)
                      </p>
                    )}
                  </div>

                  <div className="p-4 rounded" style={{ background: 'rgba(251, 146, 60, 0.1)', borderColor: '#FB923C', border: '1px solid' }}>
                    <h3 className="text-sm font-semibold mb-2" style={{ color: '#FB923C' }}>Import {tab.label}</h3>
                    <p className="text-sm mb-4" style={{ color: '#B0B0B0' }}>
                      Restore {tab.label.toLowerCase()} from a backup file. You can choose to skip existing records or update them.
                    </p>
                    <Button 
                      onClick={() => setImportDialogOpen(true)}
                      style={{ background: '#3B82F6', color: '#FFFFFF' }}
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Import {tab.label}
                    </Button>
                  </div>

                  <div className="p-4 rounded" style={{ background: 'rgba(239, 68, 68, 0.1)', borderColor: '#EF4444', border: '1px solid' }}>
                    <h3 className="text-sm font-semibold mb-2" style={{ color: '#EF4444' }}>⚠️ Important Notes</h3>
                    <ul className="text-sm space-y-1" style={{ color: '#B0B0B0' }}>
                      <li>• Backup files are encrypted for security</li>
                      <li>• Use "Skip" mode to avoid overwriting existing data</li>
                      <li>• Use "Update" mode to sync data from another deployment</li>
                      <li>• Keep backup files in a secure location</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>
              Import {activeTab === "full" ? "Complete Backup" : currentTab?.label}
            </DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              {activeTab === "full" 
                ? "Restore ALL data from a complete backup file. This will restore users, agencies, agents, flights, reports, invoices, services, and settings."
                : `Restore ${currentTab?.label.toLowerCase()} from a backup file. Choose how to handle existing records.`
              }
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={activeTab === "full" ? handleFullImport : handleImport} className="space-y-4">
            <div>
              <Label style={{ color: '#E8E8E8' }}>Backup File (.vipbackup or .json)</Label>
              <Input 
                type="file" 
                accept=".vipbackup,.json"
                onChange={(e) => setImportFile(e.target.files[0])}
                required 
                style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
              />
              <p className="text-xs mt-1" style={{ color: '#60A5FA' }}>
                💡 Use .json files for cross-instance transfers
              </p>
            </div>
            
            <div>
              <Label style={{ color: '#E8E8E8' }}>Import Mode</Label>
              <Select value={importMode} onValueChange={setImportMode}>
                <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  <SelectItem value="skip" style={{ color: '#E8E8E8' }}>
                    Skip Existing Records (Safer)
                  </SelectItem>
                  <SelectItem value="update" style={{ color: '#E8E8E8' }}>
                    Update Existing Records (Overwrite)
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                {importMode === 'skip' 
                  ? 'Existing records will be kept unchanged. Only new records will be imported.' 
                  : 'Existing records will be updated with data from the backup file.'}
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button 
                type="button"
                onClick={() => {
                  setImportDialogOpen(false);
                  setImportFile(null);
                  setImportResults(null);
                }}
                className="flex-1"
                style={{ background: '#4A5A8A', color: '#FFFFFF' }}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={importing}
                className="flex-1"
                style={{ background: '#3B82F6', color: '#FFFFFF' }}
              >
                {importing ? 'Importing...' : 'Import'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Imported Data Results Dialog - Compact Version */}
      <Dialog open={importedDataDialogOpen} onOpenChange={setImportedDataDialogOpen}>
        <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A', maxWidth: '450px' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>Import Complete</DialogTitle>
          </DialogHeader>
          
          {importResults && (
            <div className="space-y-3">
              {/* Summary Stats - Compact Grid */}
              <div className="grid grid-cols-4 gap-2">
                <div className="p-2 rounded text-center" style={{ background: 'rgba(34, 197, 94, 0.1)', borderBottom: '2px solid #22C55E' }}>
                  <p style={{ color: '#22C55E', fontSize: '1.25rem', fontWeight: 'bold' }}>{importResults.imported}</p>
                  <p style={{ color: '#22C55E', fontSize: '0.65rem' }}>Imported</p>
                </div>
                <div className="p-2 rounded text-center" style={{ background: 'rgba(59, 130, 246, 0.1)', borderBottom: '2px solid #3B82F6' }}>
                  <p style={{ color: '#3B82F6', fontSize: '1.25rem', fontWeight: 'bold' }}>{importResults.updated}</p>
                  <p style={{ color: '#3B82F6', fontSize: '0.65rem' }}>Updated</p>
                </div>
                <div className="p-2 rounded text-center" style={{ background: 'rgba(251, 146, 60, 0.1)', borderBottom: '2px solid #FB923C' }}>
                  <p style={{ color: '#FB923C', fontSize: '1.25rem', fontWeight: 'bold' }}>{importResults.skipped}</p>
                  <p style={{ color: '#FB923C', fontSize: '0.65rem' }}>Skipped</p>
                </div>
                <div className="p-2 rounded text-center" style={{ background: 'rgba(239, 68, 68, 0.1)', borderBottom: '2px solid #EF4444' }}>
                  <p style={{ color: '#EF4444', fontSize: '1.25rem', fontWeight: 'bold' }}>{importResults.errors?.length || 0}</p>
                  <p style={{ color: '#EF4444', fontSize: '0.65rem' }}>Errors</p>
                </div>
              </div>

              {/* Show Imported Items Only (if any) */}
              {importResults.imported_items && importResults.imported_items.length > 0 && (
                <div className="p-3 rounded" style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid #22C55E' }}>
                  <p style={{ color: '#22C55E', fontSize: '0.8rem', fontWeight: '600', marginBottom: '4px' }}>
                    ✅ Imported ({importResults.imported_items.length})
                  </p>
                  <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                    {importResults.imported_items.slice(0, 10).map((item, idx) => (
                      <p key={idx} style={{ color: '#FFFFFF', fontSize: '0.75rem' }}>• {item.name || item.id}</p>
                    ))}
                    {importResults.imported_items.length > 10 && (
                      <p style={{ color: '#B0B0B0', fontSize: '0.7rem' }}>...and {importResults.imported_items.length - 10} more</p>
                    )}
                  </div>
                </div>
              )}

              {/* Show Updated Items Only (if any) */}
              {importResults.updated_items && importResults.updated_items.length > 0 && (
                <div className="p-3 rounded" style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid #3B82F6' }}>
                  <p style={{ color: '#3B82F6', fontSize: '0.8rem', fontWeight: '600', marginBottom: '4px' }}>
                    🔄 Updated ({importResults.updated_items.length})
                  </p>
                  <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                    {importResults.updated_items.slice(0, 10).map((item, idx) => (
                      <p key={idx} style={{ color: '#FFFFFF', fontSize: '0.75rem' }}>• {item.name || item.id}</p>
                    ))}
                    {importResults.updated_items.length > 10 && (
                      <p style={{ color: '#B0B0B0', fontSize: '0.7rem' }}>...and {importResults.updated_items.length - 10} more</p>
                    )}
                  </div>
                </div>
              )}

              {/* Show Errors Only (if any) */}
              {importResults.errors && importResults.errors.length > 0 && (
                <div className="p-3 rounded" style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid #EF4444' }}>
                  <p style={{ color: '#EF4444', fontSize: '0.8rem', fontWeight: '600', marginBottom: '4px' }}>
                    ❌ Errors ({importResults.errors.length})
                  </p>
                  <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
                    {importResults.errors.slice(0, 5).map((err, idx) => (
                      <p key={idx} style={{ color: '#B0B0B0', fontSize: '0.7rem' }}>• {err.id || 'Unknown'}: {err.error}</p>
                    ))}
                    {importResults.errors.length > 5 && (
                      <p style={{ color: '#B0B0B0', fontSize: '0.7rem' }}>...and {importResults.errors.length - 5} more errors</p>
                    )}
                  </div>
                </div>
              )}

              {/* Close Button */}
              <Button 
                onClick={() => {
                  setImportedDataDialogOpen(false);
                  setImportResults(null);
                }}
                className="w-full"
                style={{ background: '#22C55E', color: '#FFFFFF' }}
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Backups;
